import type { UseCouponReturn, UseCouponState, AddCoupon, DeleteCoupon } from './types';
import type { ApiError, Cart, DoAddCouponParams, PlentyEvents } from '@plentymarkets/shop-api';

/**
 * @description Composable for managing coupons.
 * @returns UseCouponReturn
 * @example
 * ``` ts
 * const { addCoupon, deleteCoupon, loading } = useCoupon();
 * ```
 */
export const useCoupon: UseCouponReturn = () => {
  const { emit } = usePlentyEvent();
  const state = useState<UseCouponState>('coupon', () => ({
    loading: false,
  }));

  /**
   * @description Function for adding a coupon to curent cart.
   * @param params { DoAddCouponParams }
   * @return Cart
   * @example
   * ``` ts
   * addCoupon({
      couponCode: 'Z23ZMP'
    })
   * ```
   */
  const addCoupon: AddCoupon = async (params: DoAddCouponParams) => {
    const { send } = useNotification();
    const { fetchSession } = useFetchSession();
    state.value.loading = true;
    if (params.couponCode.trim() === '') {
      send({ message: t('coupon.pleaseProvideCoupon'), type: 'warning' });
      state.value.loading = false;
      return {} as Cart;
    }

    try {
      const { data } = await useSdk().plentysystems.doAddCoupon(params);

      if (data?.apiEvents) {
        Object.entries(data.apiEvents as PlentyEvents).forEach(([event, data]) =>
          // @ts-expect-error The type of `backend:${event}` is not assignable
          emit(`backend:${event}`, data),
        );
      }

      await fetchSession();
      send({ message: t('coupon.couponApplied'), type: 'positive' });
    } catch (err) {
      const error = err as ApiError;
      if (error.warn?.code !== undefined) {
        const key = getErrorCode(error.warn.code.toString()) ?? 'couponAlreadyUsedOrInvalidCouponCode';
        send({ message: t('error.' + key), type: 'negative' });
      } else {
        useHandleError(error);
      }
    } finally {
      state.value.loading = false;
    }
    return {} as Cart;
  };

  const deleteCoupon: DeleteCoupon = async (params: DoAddCouponParams) => {
    const { send } = useNotification();
    const { fetchSession } = useFetchSession();

    try {
      state.value.loading = true;
      const { data } = await useSdk().plentysystems.deleteCoupon(params);

      if (data) {
        if (data?.apiEvents) {
          Object.entries(data.apiEvents as PlentyEvents).forEach(([event, data]) =>
            // @ts-expect-error The type of `backend:${event}` is not assignable
            emit(`backend:${event}`, data),
          );
        }
        await fetchSession();
        send({ message: t('coupon.couponRemoved'), type: 'positive' });
      }
    } catch (error) {
      useHandleError(error as ApiError);
    } finally {
      state.value.loading = false;
    }

    return {} as Cart;
  };

  return {
    addCoupon,
    deleteCoupon,
    ...toRefs(state.value),
  };
};
